// components/tier-branch/BranchManagement.tsx
// Branch Management page: branch listing + "Create Branch" + per-branch
// edit / users / transfer. Top-level route /branch-management.
import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Card, Typography, Button, TextField, InputAdornment, CircularProgress,
  Pagination, Stack,
} from "@mui/material";
import { Add, Search } from "@mui/icons-material";
import * as api from "../../services/tierBranchApi";
import type { Branch } from "../../types/tierBranch";
import { usePermissions } from "../../hooks/usePermissions";
import { useSnack } from "./useSnack";
import BranchTable from "./BranchTable";
import CreateEditBranchForm from "./CreateEditBranchForm";
import BranchUsersDialog from "./BranchUsersDialog";
import BranchSettlementDialog from "./BranchSettlementDialog";

const LIMIT = 25;

const BranchManagement: React.FC = () => {
  const { can } = usePermissions();
  const canManage = can("canManageBranches");
  const { notify, SnackBar } = useSnack();

  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");

  // null = listing view; otherwise the form panel (create when {} sentinel).
  const [formBranch, setFormBranch] = useState<Branch | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [usersBranch, setUsersBranch] = useState<Branch | null>(null);
  const [settlementBranch, setSettlementBranch] = useState<Branch | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.listBranches({ page, limit: LIMIT, search: search || undefined });
      setBranches(r.data);
      setTotalPages(r.totalPages);
    } catch (e: any) {
      notify(e?.message || "Failed to load branches", "error");
    } finally {
      setLoading(false);
    }
  }, [page, search, notify]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setFormBranch(null);
    setShowForm(true);
  };
  const openEdit = (b: Branch) => {
    setFormBranch(b);
    setShowForm(true);
  };

  const handleToggleStatus = async (b: Branch) => {
    setBranches((prev) =>
      prev.map((x) => (x.id === b.id ? { ...x, enabled: !x.enabled } : x))
    );
    try {
      await api.updateBranchStatus(b.id, { enabled: !b.enabled });
    } catch (e: any) {
      notify(e?.message || "Failed to update status", "error");
      load();
    }
  };

  if (showForm) {
    return (
      <Box sx={{ p: 3 }}>
        <CreateEditBranchForm
          branch={formBranch}
          notify={notify}
          onCancel={() => setShowForm(false)}
          onSaved={() => {
            setShowForm(false);
            load();
          }}
        />
        {SnackBar}
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Branch Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {branches.length} branch{branches.length === 1 ? "" : "es"} on this page
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            placeholder="Search branches"
            value={search}
            onChange={(e) => {
              setPage(1);
              setSearch(e.target.value);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {canManage && (
            <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
              Create Branch
            </Button>
          )}
        </Stack>
      </Box>

      <Card sx={{ p: 1 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : branches.length === 0 ? (
          <Typography color="text.secondary" sx={{ p: 3 }}>
            No branches found.
          </Typography>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <BranchTable
              branches={branches}
              canManage={canManage}
              onEdit={openEdit}
              onViewUsers={setUsersBranch}
              onToggleStatus={handleToggleStatus}
              onSettlement={canManage ? setSettlementBranch : undefined}
            />
          </Box>
        )}
      </Card>

      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} />
        </Box>
      )}

      <BranchUsersDialog
        open={!!usersBranch}
        branch={usersBranch}
        branches={branches}
        onClose={() => setUsersBranch(null)}
        notify={notify}
      />

      <BranchSettlementDialog
        open={!!settlementBranch}
        branch={settlementBranch}
        onClose={() => setSettlementBranch(null)}
        notify={notify}
      />

      {SnackBar}
    </Box>
  );
};

export default BranchManagement;
